import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  tap,
  throwError,
} from 'rxjs';
import { DistrictSimpleDto } from 'src/app/common/dtos/district.dto';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import {
  CreateProFormAnswerPayload,
  CreateProFormItemPayload,
  CreateSectionPayload,
  UpdateProFormAnswerPayload,
  UpdateProFormIsCorrectPayload,
  UpdateProFormItemPayload,
  UpdateProFormPayload,
  UpdateSectionPayload,
} from '../../types/payloads/proforms.payloads';
import {
  CreateAnswerOptionAPIResponse,
  CreateUpdateProFormsQuestionAPIResponse,
  CreateUpdateSectionAPIResponse,
  ProFormsAPIResponse,
  UpdateAnswerOptionAPIResponse,
} from '../../types/responses/proforms.responses';
import { AlertService, AlertType } from '../alert/alert.service';

@Injectable({
  providedIn: 'root',
})
export class ProformsAdminService {
  private ProFormData: ProFormsAPIResponse | null = null;

  private ProFormSource = new BehaviorSubject(this.ProFormData);

  public currentProForm = this.ProFormSource.asObservable();

  private currentActiveQuestionData: number | null = null;

  private currentActiveQuestionSource = new BehaviorSubject(
    this.currentActiveQuestionData
  );

  public currentActiveQuestion =
    this.currentActiveQuestionSource.asObservable();

  constructor(
    private apiService: APICoreService,
    private alertService: AlertService
  ) {}

  // INCLUDES ALL EXTRA FIELDS
  getProForm(formId: number) {
    this.apiService
      .getRequest(
        `growelab/pro-forms/forms/${formId}?expand=district,districtsSharedWith.district,districtsExcludedFrom.district,submissions,sections.questions.answerOptions`
      )
      .subscribe({
        error: (error) => {
          this.ProFormSource.next(null);
          this.alertService.showAlert(`${error.error.message}`);
        },
        next: (response) => {
          if (response.item) {
            this.ProFormData = response.item;
            this.ProFormSource.next(this.ProFormData);
          } else {
            this.ProFormSource.next(null);
          }
        },
      });
  }

  // GETS ENTIRE RESPONSE BUT ONLY UPDATES THE TITLE
  updateProFormTitle(formId: number, payload: UpdateProFormPayload) {
    this.apiService
      .putRequest(
        `growelab/pro-forms/forms/${formId}?expand=district,districtsSharedWith.district,districtsExcludedFrom.district,submissions,sections.questions.answerOptions`,
        payload
      )
      .subscribe({
        error: (error) => {
          this.alertService.showAlert(
            error.error.message,
            undefined,
            AlertType.DANGER
          );
        },
        next: (response) => {
          if (response.item) {
            if (this.ProFormData) {
              this.ProFormData.title = response.item.title;
            }
            this.alertService.showAlert('Form Title Updated');
          }
        },
      });
  }

  // Replaces the current form entirely - extraFields must match getform
  updateProForm(formId: number, payload: UpdateProFormPayload) {
    this.apiService
      .putRequest(
        `growelab/pro-forms/forms/${formId}?expand=district,districtsSharedWith.district,districtsExcludedFrom.district,submissions,sections.questions.answerOptions`,
        payload
      )
      .subscribe({
        error: (error) => {
          this.alertService.showAlert(
            error.error.message,
            undefined,
            AlertType.DANGER
          );
        },
        next: (response) => {
          if (response.item) {
            this.ProFormData = response.item;
            if (this.ProFormData) {
              this.ProFormData.is_shared = Number(this.ProFormData?.is_shared);
              this.ProFormData.show_missed = Number(
                this.ProFormData?.show_missed
              );
              this.ProFormData.show_answers = Number(
                this.ProFormData?.show_answers
              );
            }

            this.ProFormSource.next(this.ProFormData);
            this.alertService.showAlert('Form Updated');
          }
        },
      });
  }

  deleteForm(formId: number): Observable<{ item: ProFormsAPIResponse }> {
    return this.apiService
      .deleteRequest(`growelab/pro-forms/forms/${formId}`)
      .pipe(
        tap({
          next: (response) => {
            if (response.item) {
              this.ProFormSource.next(null);
            }
          },
          error: (error) => {
            this.alertService.showAlert(
              error.error.message,
              undefined,
              AlertType.DANGER
            );
          },
        })
      );
  }

  // SHARING
  handleShareDistricts(incomingDistricts: DistrictSimpleDto[]) {
    const incomingDistrictIds = incomingDistricts.map(
      (district) => district.id
    );
    if (this.ProFormData?.districtsSharedWith) {
      // Handle districts to be deleted (those in form.districtsSharedWith but not in incomingDistricts)
      this.ProFormData.districtsSharedWith.forEach((share) => {
        if (
          this.ProFormData &&
          !incomingDistrictIds.includes(share.district_id)
        ) {
          this.deleteProFormShare(this.ProFormData.id, share.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
            next: () => {
              if (this.ProFormData) {
                this.getProForm(this.ProFormData.id);
              }
            },
          });
        }
      });

      // Handle new districts to be added (those in incomingDistricts but not in session.sharing)
      incomingDistricts.forEach((district) => {
        // Check if the district is not already in this.form.districtsSharedWith
        if (
          this.ProFormData &&
          !this.ProFormData.districtsSharedWith?.some(
            (share) => share.district_id === district.id
          )
        ) {
          // If it's not found, we proceed to share it
          this.shareProForm(this.ProFormData.id, district.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
            next: () => {
              if (this.ProFormData) {
                this.getProForm(this.ProFormData.id);
              }
            },
          });
        }
      });
    }
    return of(incomingDistrictIds);
  }

  handleExcludeDistricts(incomingDistricts: DistrictSimpleDto[]) {
    const incomingDistrictIds = incomingDistricts.map(
      (district) => district.id
    );
    if (this.ProFormData?.districtsExcludedFrom) {
      // Handle districts to be deleted (those in session.sharing but not in incomingDistricts)
      this.ProFormData.districtsExcludedFrom.forEach((share) => {
        if (
          !share.is_shared &&
          !incomingDistrictIds.includes(share.district_id)
        ) {
          if (this.ProFormData) {
            this.deleteProFormShare(this.ProFormData.id, share.id).subscribe({
              error: (error) => {
                this.alertService.showAlert(
                  error.error.message,
                  undefined,
                  AlertType.DANGER
                );
              },
              next: () => {
                if (this.ProFormData) {
                  this.getProForm(this.ProFormData.id);
                }
              },
            });
          }
        }
      });
      // Handle new districts to be added (those in incomingDistricts but not in session.sharing)
      incomingDistricts.forEach((district) => {
        if (
          this.ProFormData &&
          !this.ProFormData.districtsExcludedFrom?.some(
            (share) => share.district_id === district.id
          )
        ) {
          this.unshareProForm(this.ProFormData.id, district.id).subscribe({
            error: (error) => {
              this.alertService.showAlert(
                error.error.message,
                undefined,
                AlertType.DANGER
              );
            },
            next: () => {
              if (this.ProFormData) {
                this.getProForm(this.ProFormData.id);
              }
            },
          });
        }
      });
    }

    return of(incomingDistrictIds);
  }

  // REMOVE WHEN DONE

  shareProForm(formId: number, districtId: number) {
    return this.apiService
      .postRequest(`growelab/pro-forms/${formId}/share/${districtId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  unshareProForm(formId: number, districtId: number) {
    return this.apiService
      .postRequest(`growelab/pro-forms/${formId}/unshare/${districtId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  deleteProFormShare(formId: number, shareId: number) {
    return this.apiService
      .deleteRequest(`growelab/pro-forms/${formId}/share/${shareId}`)
      .pipe(catchError((err) => throwError(() => err)));
  }

  /// ////

  // SECTIONS

  createSection(
    formId: number,
    payload: CreateSectionPayload
  ): Observable<CreateUpdateSectionAPIResponse> {
    return this.apiService.postRequest(
      `growelab/pro-forms/form-sections/${formId}/questions`,
      payload
    );
  }

  updateSection(
    sectionId: number,
    payload: UpdateSectionPayload
  ): Observable<CreateUpdateSectionAPIResponse> {
    return this.apiService.putRequest(
      `growelab/pro-forms/form-sections/${sectionId}`,
      payload
    );
  }

  deleteSection(sectionId: number) {
    return this.apiService.deleteRequest(
      `growelab/pro-forms/form-sections/${sectionId}`
    );
  }

  // Items - currently only have Question items, more types to be added later

  createQuestion(sectionId: number, payload: CreateProFormItemPayload) {
    this.apiService
      .postRequest(`growelab/pro-forms/section/${sectionId}/questions`, payload)
      .subscribe(
        (newQuestionResponse: CreateUpdateProFormsQuestionAPIResponse) => {
          if (this.ProFormData?.sections) {
            const sectionIndex = this.ProFormData.sections.findIndex(
              (section) => section.id === sectionId
            );
            this.ProFormData.sections[sectionIndex].questions.push(
              newQuestionResponse.item
            );
            this.ProFormSource.next(this.ProFormData);
            this.setCurrentQuestion(newQuestionResponse.item.id);
          }
        }
      );
  }

  updateQuestion(
    questionId: number,
    sectionId: number,
    payload: UpdateProFormItemPayload
  ) {
    this.apiService
      .putRequest(`growelab/pro-forms/form-questions/${questionId}`, payload)
      .subscribe((response: CreateUpdateProFormsQuestionAPIResponse) => {
        if (this.ProFormData) {
          if (this.ProFormData?.sections) {
            const sectionIndex = this.ProFormData.sections.findIndex(
              (section) => section.id === sectionId
            );
            if (sectionIndex !== -1) {
              const questionsArr =
                this.ProFormData.sections[sectionIndex].questions || [];
              const questionIndex = questionsArr.findIndex(
                (question) => question.id === questionId
              );
              if (questionIndex !== -1) {
                const prev = questionsArr[questionIndex];

                // Merge previous and updated to avoid dropping properties the API might omit
                const updated = { ...prev, ...response.item } as typeof prev;

                // Normalize required to a number (0/1) safely
                const normalizedRequired = Number(updated.required);
                updated.required = Number.isNaN(normalizedRequired)
                  ? 0
                  : normalizedRequired;

                this.ProFormData.sections[sectionIndex].questions[
                  questionIndex
                ] = updated;
                this.ProFormSource.next(this.ProFormData);
              }
            }
          }
        }
      });
  }

  deleteQuestion(questionId: number, sectionId: number) {
    this.apiService
      .deleteRequest(`growelab/pro-forms/form-questions/${questionId}`)
      .subscribe((resp) => {
        if (resp) {
          if (this.ProFormData?.sections) {
            const sectionIndex = this.ProFormData.sections.findIndex(
              (section) => section.id === sectionId
            );
            this.ProFormData.sections[sectionIndex].questions =
              this.ProFormData.sections[sectionIndex].questions.filter(
                (question) => question.id !== questionId
              );
          }
        }
      });
  }

  setCurrentQuestion(questionId: number) {
    this.currentActiveQuestionSource.next(questionId);
  }

  // ANSWER OPTIONS

  createAnswerOption(
    questionId: number,
    payload: CreateProFormAnswerPayload
  ): Observable<CreateAnswerOptionAPIResponse> {
    return this.apiService.postRequest(
      `growelab/pro-forms/question/${questionId}/answers`,
      payload
    );
  }

  updateAnswerOption(
    answerId: number,
    payload: UpdateProFormAnswerPayload
  ): Observable<UpdateAnswerOptionAPIResponse> {
    return this.apiService
      .putRequest(
        `growelab/pro-forms/form-question-answer-options/${answerId}`,
        payload
      )
      .pipe(
        tap({
          next: (response) => {
            if (response.item) {
              this.ProFormData?.sections?.forEach((section) => {
                section.questions.forEach((question) => {
                  const answerIndex = question.answerOptions.findIndex(
                    (answer) => answer.id === answerId
                  );
                  if (answerIndex !== -1) {
                    question.answerOptions[answerIndex].answer =
                      response.item.answer;
                  }
                });
              });
              this.ProFormSource.next(this.ProFormData);
            }
          },
          error: (error) => {
            this.alertService.showAlert(
              error.error.message,
              undefined,
              AlertType.DANGER
            );
          },
        })
      );
  }

  deleteAnswerOption(answerId: number) {
    return this.apiService
      .deleteRequest(
        `growelab/pro-forms/form-question-answer-options/${answerId}`
      )
      .pipe(
        tap({
          next: (response) => {
            if (response.item) {
              this.ProFormData?.sections?.forEach((section) => {
                section.questions.forEach((question) => {
                  const answerIndex = question.answerOptions.findIndex(
                    (answer) => answer.id === answerId
                  );
                  if (answerIndex !== -1) {
                    question.answerOptions.splice(answerIndex, 1);
                  }
                });
              });
              this.ProFormSource.next(null);
            }
          },
          error: (error) => {
            this.alertService.showAlert(
              error.error.message,
              undefined,
              AlertType.DANGER
            );
          },
        })
      );
  }

  updateCorrectAnswerOption(
    answerId: number,
    payload: UpdateProFormIsCorrectPayload
  ): Observable<UpdateAnswerOptionAPIResponse> {
    return this.apiService
      .putRequest(
        `growelab/pro-forms/form-question-answer-options/${answerId}`,
        payload
      )
      .pipe(
        tap({
          next: (response) => {
            if (response.item) {
              this.ProFormData?.sections?.forEach((section) => {
                section.questions.forEach((question) => {
                  const answerIndex = question.answerOptions.findIndex(
                    (answer) => answer.id === answerId
                  );
                  if (answerIndex !== -1) {
                    question.answerOptions[answerIndex].is_correct =
                      +response.item.is_correct;
                  }
                });
              });
              this.ProFormSource.next(this.ProFormData);
            }
          },
          error: (error) => {
            this.alertService.showAlert(
              error.error.message,
              undefined,
              AlertType.DANGER
            );
          },
        })
      );
  }

  setActiveQuestion(itemId: number) {
    this.currentActiveQuestionSource.next(itemId);
  }
}
